import React from 'react';
import { money0 } from '../../utils/money';
import {
  initials,
  meetingPeriodLabels,
  pct,
  revenueOf,
  capturedRevenueOf,
  teamRevenueTotals,
  ytdDeliveredOf,
} from './metrics';
import { toneFor } from './personTone';
import './TeamOverview.css';

// The overview is for scanning; exact ringgit amounts remain in each person's note.
const shortMoney = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const amount = Number(value);
  if (Math.abs(amount) >= 999500) return `RM ${(amount / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
  if (Math.abs(amount) >= 1000) return `RM ${Math.round(amount / 1000)}k`;
  return money0(amount);
};

function TargetProgress({ name, measure, actual, target, percentage }) {
  const hasProgress = percentage != null && Number.isFinite(percentage);
  const label = actual == null
    ? (measure === 'Delivered' ? 'Awaiting delivery' : 'Awaiting capture')
    : target == null ? 'No target set'
      : Number(target) === 0 ? 'Target RM 0'
        : `Target ${shortMoney(target)}`;
  return (
    <div className={`ml-team-measure ${measure === 'Captured' ? 'is-captured' : 'is-delivered'}`}>
      <span className="ml-team-measure-label">{measure}</span>
      <strong className="ml-team-measure-actual">{shortMoney(actual)}</strong>
      <span className="ml-team-measure-target">{label}</span>
      {hasProgress ? (
        <div className="ml-team-measure-track" role="progressbar"
             aria-label={`${name} ${measure.toLowerCase()}`}
             aria-valuemin="0" aria-valuemax="100"
             aria-valuenow={Math.max(0, Math.min(Math.round(percentage), 100))}
             aria-valuetext={`${Math.round(percentage)}% of target`}>
          <i style={{ width: `${Math.max(0, Math.min(percentage, 100))}%` }} />
        </div>
      ) : <span className="ml-team-measure-no-track" />}
      {hasProgress && <b className={`ml-team-measure-pct ${percentage >= 100 ? 'is-good' : 'is-risk'}`}>
        {Math.round(percentage)}%
      </b>}
    </div>
  );
}

export default function TeamOverview({ sections, meetingDate, today }) {
  const totals = teamRevenueTotals(sections);
  const periods = meetingPeriodLabels(meetingDate, today);
  const rows = (sections || [])
    .map(section => {
      const actual = revenueOf(section);
      const target = section?.checkins?.total?.floor;
      const percentage = pct(actual, target);
      const tracksBookings = section?.checkins?.total?.source === 'booking';
      const captured = tracksBookings ? capturedRevenueOf(section) : null;
      const capturedTarget = tracksBookings ? section?.checkins?.total?.capturedTarget ?? null : null;
      return {
        id: section.salesperson_id,
        name: section.salesperson_name,
        actual,
        target,
        percentage,
        tracksBookings,
        captured,
        capturedTarget,
        capturedPercentage: pct(captured, capturedTarget),
        ytd: ytdDeliveredOf(section),
        tone: toneFor(section),
      };
    })
    .filter(row => row.actual != null || row.ytd != null)
    .sort((a, b) => Number(b.actual ?? -Infinity) - Number(a.actual ?? -Infinity));

  if (!rows.length && totals.captured == null && totals.delivered == null && totals.ytdDelivered == null) return null;

  return (
    <section className="ml-team-overview" aria-labelledby="team-overview-title">
      <header className="ml-team-overview-head">
        <h2 id="team-overview-title">Team overview</h2>
        <div className="ml-team-overview-meta">
          <div className="ml-team-metric-key" aria-label="Metric colours">
            <span className="is-delivered">Delivered</span>
            <span className="is-captured">Captured</span>
          </div>
          <span className="ml-team-period">{periods.month}</span>
        </div>
      </header>

      <div className="ml-team-snapshot" aria-label="Team snapshot">
        <div className="ml-team-snapshot-grid">
          <article data-testid="team-total-captured">
            <span>Captured this month</span>
            <strong>{money0(totals.captured)}</strong>
            <small>{periods.month}</small>
          </article>
          <article data-testid="team-total-delivered">
            <span>Delivered this month</span>
            <strong>{money0(totals.delivered)}</strong>
            <small>{periods.month}</small>
          </article>
          <article className="is-ytd" data-testid="team-total-ytd-delivered">
            <span>YTD delivered</span>
            <strong>{money0(totals.ytdDelivered)}</strong>
            <small>{periods.ytd}</small>
          </article>
        </div>
      </div>

      {!!rows.length && (
        <div className="ml-team-scoreboard">
          <div className="ml-team-score-head" aria-hidden="true">
            <span>Salesperson</span>
            <span>{periods.month} targets</span>
            <span>YTD delivered</span>
          </div>
          {rows.map(row => (
              <article className="ml-team-score-row" data-testid="team-score-row"
                       key={row.id ?? row.name} style={{ '--tone': row.tone }}>
                <div className="ml-team-score-person">
                  <span>{initials(row.name)}</span>
                  <b>{row.name}</b>
                </div>
                <div className="ml-team-measures">
                  <TargetProgress name={row.name} measure="Delivered" actual={row.actual}
                                  target={row.target} percentage={row.percentage} />
                  {row.tracksBookings && (
                    <TargetProgress name={row.name} measure="Captured" actual={row.captured}
                                    target={row.capturedTarget} percentage={row.capturedPercentage} />
                  )}
                </div>
                <strong className="ml-team-score-ytd" data-testid="ytd-person-card">{shortMoney(row.ytd)}</strong>
              </article>
          ))}
        </div>
      )}
    </section>
  );
}
